import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';

import { evaluatePeriod } from '../../store/slices/periodsSlice';
import { pushToast } from '../../store/slices/uiSlice';
import { formatDate } from '../../utils/format';

/**
 * Confirm dialog for kicking off an evaluation. Offers a "dry-run" toggle so
 * admins can preview the result without mutating evaluation/leaderboard
 * tables (backend wraps the work in a SAVEPOINT + rollback — analiz §4.4).
 *
 * Props:
 *   period           — açılacak dönem (initial selection; truthy = göster)
 *   onClose          — iptal / başarı / overlay click ile çağrılır
 *   availablePeriods — OPSİYONEL. Birden fazla aktif dönem varsa Dashboard
 *                      gibi global akışlar bu listeyi geçer; modal içinde
 *                      dropdown çıkar, admin hangi dönem için çalıştıracağını
 *                      diyalogu kapatmadan değiştirebilir. Tek dönem ise
 *                      (veya prop verilmediyse) dropdown gizli, period prop'u
 *                      sabit kalır — Periods listesi/PeriodDetail akışları
 *                      eskisi gibi davranır.
 *   onJobQueued      — OPSİYONEL. Backend job kuyruğa alındıktan SONRA
 *                      `{ jobId, periodId, dryRun }` ile çağrılır. Dashboard
 *                      bu callback'i kullanarak polling başlatır ve sonuç
 *                      gelince admin'e geri bildirim verir.
 */
export default function EvaluateDialog({ period, onClose, availablePeriods, onJobQueued }) {
  const dispatch = useDispatch();
  const [dryRun, setDryRun] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Diyalog içinde "şu an seçili olan" dönem — initial olarak prop'tan gelir.
  // availablePeriods.length > 1 ise dropdown ile değiştirilebilir.
  const [activeId, setActiveId] = useState(() => period?.id ?? null);

  // Diyalog yeni bir period için açılınca toggle ve seçimi sıfırla — eski
  // session'ın değerleri sızmasın.
  useEffect(() => {
    if (period) {
      setDryRun(false);
      setActiveId(period.id);
    }
  }, [period?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!period) return null;

  // Modal içinde gösterilen efektif dönem — dropdown'dan seçim yapıldıysa
  // listeden çekiyoruz, yoksa orijinal prop'u kullanıyoruz.
  const candidates = Array.isArray(availablePeriods) ? availablePeriods : [];
  const showSelector = candidates.length > 1;
  const effectivePeriod =
    (showSelector && candidates.find((p) => String(p.id) === String(activeId))) || period;

  const handleConfirm = async () => {
    setSubmitting(true);
    const res = await dispatch(evaluatePeriod({ id: effectivePeriod.id, dry_run: dryRun }));
    setSubmitting(false);
    if (res.meta.requestStatus === 'fulfilled') {
      const jobId = res.payload?.job_id;
      dispatch(
        pushToast({
          kind: 'success',
          message: dryRun
            ? `"${effectivePeriod.name}" için dry-run kuyruğa alındı${
                jobId ? ` (job: ${jobId})` : ''
              }`
            : `"${effectivePeriod.name}" hesaplama kuyruğa alındı`,
        }),
      );
      // Caller polling başlatabilsin diye job bilgisini ilet — set edilmemişse
      // backward-compatible olarak yok sayılır.
      if (typeof onJobQueued === 'function' && jobId) {
        onJobQueued({ jobId, periodId: effectivePeriod.id, dryRun });
      }
      onClose(dryRun); // dry-run ise true geçir → üst bileşen meta önbelleğini temizler
    } else {
      dispatch(pushToast({ kind: 'error', message: 'Hesaplama başlatılamadı' }));
    }
  };

  return (
    <Modal
      open={Boolean(period)}
      onClose={submitting ? () => {} : onClose}
      title="Hesaplama Başlat"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            İptal
          </Button>
          <Button
            loading={submitting}
            variant={dryRun ? 'secondary' : 'primary'}
            onClick={handleConfirm}
          >
            {dryRun ? 'Dry-run Başlat' : 'Hesaplamayı Başlat'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {showSelector ? (
          <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm">
            <label className="block text-xs font-medium text-violet-800 mb-1.5">
              {candidates.length} aktif dönem var — hangisi için çalıştırılsın?
            </label>
            <select
              value={String(activeId ?? '')}
              onChange={(e) => setActiveId(Number(e.target.value))}
              disabled={submitting}
              className="w-full rounded-md border border-violet-300 bg-white px-2 py-1.5 text-sm font-medium text-violet-900 outline-none focus:ring-2 focus:ring-violet-300 cursor-pointer"
            >
              {candidates.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>
            <p className="mt-1.5 text-[11px] text-violet-700">
              {formatDate(effectivePeriod.start_date)} — {formatDate(effectivePeriod.end_date)}
              {effectivePeriod.task_count != null ? ` · ${effectivePeriod.task_count} görev` : ''}
            </p>
          </div>
        ) : (
          <div className="rounded-lg bg-neutral-50 p-3 text-sm text-neutral-700">
            <p>
              <span className="font-medium">Dönem:</span> {effectivePeriod.name}
            </p>
            <p className="mt-1 text-xs text-neutral-500">
              {formatDate(effectivePeriod.start_date)} — {formatDate(effectivePeriod.end_date)}
              {effectivePeriod.task_count != null ? ` · ${effectivePeriod.task_count} görev` : ''}
            </p>
          </div>
        )}

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm">
            <span className="block font-medium text-neutral-800">
              Dry-run (önizleme)
            </span>
            <span className="block text-xs text-neutral-500">
              Hesaplama tam olarak çalışır ama sonuçlar yazılmaz. Etkilenen
              kullanıcı sayısı ve süre job kaydında görünür; gerçek veriler
              değişmez.
            </span>
          </span>
        </label>

        {!dryRun && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Gerçek çalıştırma — mevcut <code>evaluations</code> ve{' '}
            <code>leaderboard_snapshots</code> silinip yeniden oluşturulur.
          </div>
        )}
      </div>
    </Modal>
  );
}
